import oktaAuth from '../auth/oktaAuth';
import { normalizeCatalogMode, type CatalogMode } from '../types/catalogMode';

export type ProjectType = 'optimization' | 'consultation';
export type { CatalogMode } from '../types/catalogMode';

export interface SophiaThreadState {
  app_state?: Record<string, unknown>;
  ['current-message']?: string;
  catalog_mode?: CatalogMode;
  [key: string]: unknown;
}

export interface CreationMetadata {
  project_type?: ProjectType;
  catalog_mode?: CatalogMode;
  incognito?: boolean;
  [key: string]: unknown;
}

export interface SophiaThread {
  uuid: string;
  collection_uuid?: string;
  local_memory?: {
    thread_state?: SophiaThreadState;
    creation_metadata?: CreationMetadata;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

export interface SophiaOrganizationInfo {
  uuid: string;
  name?: string;
  is_admin?: boolean;
  [key: string]: unknown;
}

function getRawCreationMetadata(thread: SophiaThread | null | undefined): Record<string, unknown> | undefined {
  const localMemory = thread?.local_memory as Record<string, unknown> | undefined;
  const direct = localMemory?.creation_metadata;
  if (direct && typeof direct === 'object' && !Array.isArray(direct)) {
    return direct as Record<string, unknown>;
  }

  const nested = localMemory?.thread_creation_metadata;
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    const creationMetadata = (nested as Record<string, unknown>).creation_metadata;
    if (creationMetadata && typeof creationMetadata === 'object' && !Array.isArray(creationMetadata)) {
      return creationMetadata as Record<string, unknown>;
    }
    return nested as Record<string, unknown>;
  }

  return undefined;
}

export function getProjectTypeFromThread(thread: SophiaThread | null | undefined): ProjectType | undefined {
  const creationMetadata = getRawCreationMetadata(thread);
  const fromCreation = creationMetadata?.project_type;
  if (fromCreation === 'optimization' || fromCreation === 'consultation') {
    return fromCreation;
  }

  const fromState = thread?.local_memory?.thread_state?.project_type;
  if (fromState === 'optimization' || fromState === 'consultation') {
    return fromState;
  }

  return undefined;
}

export function getCatalogModeFromThread(thread: SophiaThread | null | undefined): CatalogMode | undefined {
  if (!thread) {
    return undefined;
  }

  const creationMetadata = getRawCreationMetadata(thread);
  const fromCreation = normalizeCatalogMode(creationMetadata?.catalog_mode);
  if (fromCreation) {
    return fromCreation;
  }

  const threadState = thread?.local_memory?.thread_state;
  const fromState = normalizeCatalogMode(threadState?.catalog_mode);
  if (fromState) {
    return fromState;
  }

  const fromAppState = normalizeCatalogMode(threadState?.app_state?.catalog_mode);
  return fromAppState ?? 'AI';
}

const sophiaBaseUrl = import.meta.env.VITE_SOPHIA_API_URL as string | undefined;
const sophiaCollectionId = import.meta.env.VITE_SOPHIA_COLLECTION_ID as string | undefined;
const sophiaApiKey = import.meta.env.VITE_SOPHIA_API_KEY as string | undefined;
const sophiaOrgId = import.meta.env.VITE_SOPHIA_ORG_ID as string | undefined;

function ensureBaseUrl(): string {
  if (!sophiaBaseUrl) {
    throw new Error('VITE_SOPHIA_API_URL is not configured');
  }
  return sophiaBaseUrl.replace(/\/$/, '');
}

async function buildHeaders(): Promise<HeadersInit> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  
  // Add API key if available
  if (sophiaApiKey) {
    headers['api-key'] = sophiaApiKey;
  }
  
  // Always try to add Authorization header when available
  // (needed for encrypted message decryption even when using api-key)
  try {
    const accessToken = await oktaAuth.getAccessToken();
    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }
  } catch (error) {
    console.warn('Failed to get Okta access token:', error);
    // Continue without authorization header - let the backend handle the missing auth
  }
  
  headers['predictive-mode'] = 'true';
  return headers;
}

/**
 * Refreshes the Okta access token if it's expired
 * @returns true if token was refreshed successfully, false otherwise
 */
async function refreshAccessToken(): Promise<boolean> {
  // Skip token refresh if using API key
  if (sophiaApiKey) {
    return false;
  }

  try {
    // Check if oktaAuth has tokenManager (not available in debug mode)
    if (!oktaAuth.tokenManager) {
      console.warn('Token manager not available, skipping token refresh');
      return false;
    }

    console.log('Attempting to refresh access token...');
    
    // Try to renew the access token
    const newToken = await oktaAuth.tokenManager.renew('accessToken');
    
    if (newToken) {
      console.log('Access token refreshed successfully');
      return true;
    }
    
    console.warn('Failed to refresh access token');
    return false;
  } catch (error) {
    console.error('Error refreshing access token:', error);
    
    // If refresh fails, the user might need to re-authenticate
    // Check if we should redirect to login
    if (error instanceof Error && error.message.includes('login_required')) {
      console.log('Re-authentication required, redirecting to login...');
      try {
        await oktaAuth.signInWithRedirect?.();
      } catch (redirectError) {
        console.error('Failed to redirect to login:', redirectError);
      }
    }
    
    return false;
  }
}

/**
 * Wrapper around fetch that handles 401 responses by refreshing the token and retrying
 */
async function fetchWithTokenRefresh(url: string, options: RequestInit): Promise<Response> {
  // Make the initial request
  let response = await fetch(url, options);
  
  // If we get a 401 and we're using Okta auth (not API key), try to refresh the token
  if (response.status === 401 && !sophiaApiKey) {
    console.log('Received 401 response, attempting token refresh...');
    
    const refreshed = await refreshAccessToken();
    
    if (refreshed) {
      // Rebuild headers with the new token
      const newHeaders = await buildHeaders();
      const newOptions = {
        ...options,
        headers: newHeaders,
      };
      
      // Retry the request with the new token
      console.log('Retrying request with refreshed token...');
      response = await fetch(url, newOptions);
      
      if (response.ok) {
        console.log('Request succeeded after token refresh');
      } else {
        console.warn('Request failed even after token refresh:', response.status);
      }
    } else {
      console.warn('Token refresh failed, returning original 401 response');
    }
  }
  
  return response;
}

export async function createThread(
  title = 'predictive thread',
  creationMetadata?: CreationMetadata
): Promise<string> {
  const baseUrl = ensureBaseUrl();
  if (!sophiaCollectionId) {
    throw new Error('VITE_SOPHIA_COLLECTION_ID is not configured');
  }

  const url = new URL(`${baseUrl}/threads`);
  url.searchParams.set('collection_uuid', sophiaCollectionId);
  url.searchParams.set('title', title);

  const response = await fetchWithTokenRefresh(url.toString(), {
    method: 'POST',
    headers: await buildHeaders(),
    body: creationMetadata ? JSON.stringify({ creation_metadata: creationMetadata }) : undefined,
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Failed to create thread: ${response.status} ${text}`);
  }

  const body = (await response.json()) as { uuid: string };
  return body.uuid;
}

/**
 * SPJ-1372: il progetto e' in uso da un altro utente (HTTP 423 dal backend). Lo si tratta a parte
 * perche' non e' un errore da mostrare: il progetto non va aperto e va detto chi lo sta usando.
 */
export class ProjectLockedError extends Error {
  readonly lockedByName: string | null;
  readonly lockedByEmail: string | null;

  constructor(lockedByName: string | null, lockedByEmail: string | null) {
    super(`Progetto in uso da ${lockedByName || lockedByEmail || 'un altro utente'}`);
    this.name = 'ProjectLockedError';
    this.lockedByName = lockedByName;
    this.lockedByEmail = lockedByEmail;
  }
}

async function throwIfProjectLocked(response: Response): Promise<void> {
  if (response.status !== 423) return;
  let detail: { locked_by_name?: string | null; locked_by_email?: string | null } = {};
  try {
    const body = (await response.json()) as { detail?: typeof detail };
    detail = body?.detail ?? {};
  } catch {
    // corpo non JSON: si segnala comunque il lock, senza nome
  }
  throw new ProjectLockedError(detail.locked_by_name ?? null, detail.locked_by_email ?? null);
}

export async function getThread(threadId: string): Promise<SophiaThread> {
  const baseUrl = ensureBaseUrl();
  const response = await fetchWithTokenRefresh(`${baseUrl}/threads/${threadId}`, {
    headers: await buildHeaders(),
  });
  await throwIfProjectLocked(response);
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Failed to fetch thread: ${response.status} ${text}`);
  }
  return (await response.json()) as SophiaThread;
}

export async function getCurrentUserOrganizations(): Promise<SophiaOrganizationInfo[]> {
  const baseUrl = ensureBaseUrl();
  const response = await fetchWithTokenRefresh(`${baseUrl}/organizations/for-user/me`, {
    headers: await buildHeaders(),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Failed to fetch user organizations: ${response.status} ${text}`);
  }

  const body = (await response.json()) as unknown;
  return Array.isArray(body) ? (body as SophiaOrganizationInfo[]) : [];
}

export interface PerformActionOptions {
  threadId: string;
  messageId: string;
  actionName: string;
  params?: Record<string, unknown>;
}

export async function performAction({
  threadId,
  messageId,
  actionName,
  params,
}: PerformActionOptions): Promise<unknown> {
  const baseUrl = ensureBaseUrl();
  const url = new URL(`${baseUrl}/threads/${threadId}/${messageId}/actions/${actionName}`);
  if (sophiaOrgId) {
    url.searchParams.set('current_org_uuid', sophiaOrgId);
  }
  const response = await fetchWithTokenRefresh(url.toString(), {
    method: 'POST',
    headers: await buildHeaders(),
    body: JSON.stringify(params ?? {}),
  });

  await throwIfProjectLocked(response);
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Failed to perform action ${actionName}: ${response.status} ${text}`);
  }

  const body = (await response.json()) as { data?: unknown };
  return body.data;
}

export async function performStreamingAction({
  threadId,
  messageId,
  actionName,
  params,
  onDelta,
}: PerformActionOptions & { onDelta: (text: string) => void }): Promise<string> {
  const baseUrl = ensureBaseUrl();
  const url = new URL(`${baseUrl}/threads/${threadId}/${messageId}/actions/${actionName}`);
  if (sophiaOrgId) {
    url.searchParams.set('current_org_uuid', sophiaOrgId);
  }
  const response = await fetchWithTokenRefresh(url.toString(), {
    method: 'POST',
    headers: await buildHeaders(),
    body: JSON.stringify(params ?? {}),
  });

  await throwIfProjectLocked(response);
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Failed to perform action ${actionName}: ${response.status} ${text}`);
  }

  const contentType = response.headers.get('content-type') || '';

  const normalizeExplanationText = (value: unknown): string => {
    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value) as { text?: unknown };
        if (typeof parsed?.text === 'string') return parsed.text;
      } catch {
        return value;
      }
      return value;
    }
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      const maybeText = (value as { text?: unknown }).text;
      if (typeof maybeText === 'string') return maybeText;
    }
    return '';
  };

  // Non-streaming response (cached result)
  if (!contentType.includes('text/event-stream')) {
    const body = (await response.json()) as { data?: { explanation?: unknown } };
    const explanation = normalizeExplanationText(body.data?.explanation);
    onDelta(explanation);
    return explanation;
  }

  // Streaming response
  const reader = response.body?.getReader();
  if (!reader) throw new Error('No response body');
  const decoder = new TextDecoder();
  let accumulated = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    const chunk = decoder.decode(value, { stream: true });
    for (const line of chunk.split('\n').filter(Boolean)) {
      try {
        const parsed = JSON.parse(line) as { answer?: string };
        if (parsed.answer && parsed.answer !== '<ENDMS/>') {
          accumulated += parsed.answer;
          onDelta(accumulated);
        }
      } catch {
        // skip unparseable lines
      }
    }
  }
  return accumulated;
}

export interface ThreadSummary {
  uuid: string;
  title: string;
  "thread-created": number;
  status: string;
  "last-message": number;
}

export type ThreadsByCollectionResponse = ThreadSummary[];

export async function getThreadsByCollection(collectionUuid: string): Promise<string[]> {
  const baseUrl = ensureBaseUrl();
  const url = `${baseUrl}/threads/by-collection/${collectionUuid}`;
  
  const response = await fetchWithTokenRefresh(url, {
    headers: await buildHeaders(),
  });
  
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Failed to fetch threads by collection: ${response.status} ${text}`);
  }
  
  const body = (await response.json()) as ThreadsByCollectionResponse;
  
  // Extract UUIDs from the array of thread objects
  return body.map(thread => thread.uuid);
}

/**
 * Shape of each element returned by `/threads/by-collection/{uuid}/projects` (SPJ-1372).
 * The backend uses kebab/snake keys; this is the raw wire format before mapping.
 */
interface ProjectSummaryRaw {
  uuid: string;
  title?: string | null;
  'thread-created'?: number | null;
  status?: string | null;
  incognito?: boolean | null;
  'last-message'?: number | null;
  project_type?: string | null;
  optimization_id?: string | null;
  consegna?: string | null;
  macrotema?: string | null;
  gender?: string | null;
  catalog_mode?: string | null;
  /** SPJ-1372: stagione numerica (es. "272") esposta dall'endpoint lista. */
  season?: string | null;
  'last-edit-time'?: string | null;
  'pending-config-id'?: string | null;
  'optim-dataset'?: Record<string, unknown> | null;
  history?: unknown[] | null;
  creator_uuid?: string | null;
  creator_name?: string | null;
  creator_email?: string | null;
  /** SPJ-1372: ultimo utente che ha modificato il progetto (solo ottimizzazioni). */
  last_editor_name?: string | null;
  last_editor_email?: string | null;
  /** Timestamp in ms come stringa. */
  last_editor_time?: string | null;
  /** SPJ-1372: valorizzati se il progetto e' in uso da un ALTRO utente. */
  locked_by_name?: string | null;
  locked_by_email?: string | null;
}

/**
 * Project summary (= Sophia thread) returned in a single query by the new backend endpoint
 * (SPJ-1372). camelCase mapping of the raw kebab/snake payload.
 */
export interface ProjectSummary {
  threadId: string;
  title: string | null;
  threadCreated: number | null;
  status: string | null;
  incognito: boolean;
  lastMessage: number | null;
  projectType: ProjectType | null;
  optimizationId: string | null;
  consegna: string | null;
  macrotema: string | null;
  gender: string | null;
  catalogMode: CatalogMode | null;
  /** SPJ-1372: stagione numerica come stringa (es. "272"), usata dal filtro "Stagione". */
  season: string | null;
  /** Numeric timestamp as a string, kept raw (parsed to a number where needed). */
  lastEditTime: string | null;
  pendingConfigId: string | null;
  optimDataset: Record<string, unknown> | null;
  history: unknown[] | null;
  creatorUuid: string | null;
  creatorName: string | null;
  creatorEmail: string | null;
  lastEditorName: string | null;
  lastEditorEmail: string | null;
  lastEditorTime: number | null;
  lockedByName: string | null;
  lockedByEmail: string | null;
}

function toProjectType(value: unknown): ProjectType | null {
  return value === 'optimization' || value === 'consultation' ? value : null;
}

function mapProjectSummary(raw: ProjectSummaryRaw): ProjectSummary {
  return {
    threadId: raw.uuid,
    title: raw.title ?? null,
    threadCreated: raw['thread-created'] ?? null,
    status: raw.status ?? null,
    incognito: raw.incognito === true,
    lastMessage: raw['last-message'] ?? null,
    projectType: toProjectType(raw.project_type),
    optimizationId: raw.optimization_id ?? null,
    consegna: raw.consegna ?? null,
    macrotema: raw.macrotema ?? null,
    gender: raw.gender ?? null,
    catalogMode: normalizeCatalogMode(raw.catalog_mode) ?? null,
    season: raw.season ?? null,
    lastEditTime: raw['last-edit-time'] ?? null,
    pendingConfigId: raw['pending-config-id'] ?? null,
    optimDataset: raw['optim-dataset'] ?? null,
    history: raw.history ?? null,
    creatorUuid: raw.creator_uuid ?? null,
    creatorName: raw.creator_name ?? null,
    creatorEmail: raw.creator_email ?? null,
    lastEditorName: raw.last_editor_name ?? null,
    lastEditorEmail: raw.last_editor_email ?? null,
    lastEditorTime: raw.last_editor_time ? Number(raw.last_editor_time) || null : null,
    lockedByName: raw.locked_by_name ?? null,
    lockedByEmail: raw.locked_by_email ?? null,
  };
}

/**
 * SPJ-1372: single-query listing of all projects (threads) in a collection, replacing the old
 * N+1 (`getThreadsByCollection` + a `getThread` per project).
 */
export async function getProjectsByCollection(collectionUuid: string): Promise<ProjectSummary[]> {
  const baseUrl = ensureBaseUrl();
  const url = `${baseUrl}/threads/by-collection/${collectionUuid}/projects`;

  const response = await fetchWithTokenRefresh(url, {
    headers: await buildHeaders(),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Failed to fetch projects by collection: ${response.status} ${text}`);
  }

  const body = (await response.json()) as unknown;
  if (!Array.isArray(body)) {
    return [];
  }
  return (body as ProjectSummaryRaw[])
    .filter((raw) => raw && typeof raw.uuid === 'string')
    .map(mapProjectSummary);
}

/** SPJ-1372: heartbeat del lock del progetto aperto (lancia ProjectLockedError se in uso da altri). */
export async function refreshProjectLock(threadId: string): Promise<void> {
  const baseUrl = ensureBaseUrl();
  const response = await fetchWithTokenRefresh(`${baseUrl}/threads/${threadId}/lock`, {
    method: 'POST',
    headers: await buildHeaders(),
  });
  await throwIfProjectLocked(response);
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Failed to refresh project lock: ${response.status} ${text}`);
  }
}

/**
 * SPJ-1372: rilascia il lock del progetto. `keepalive` fa arrivare la richiesta anche quando la
 * pagina si sta chiudendo; e' best-effort (se non arriva, il lock scade da solo in ~2 minuti).
 */
export async function releaseProjectLock(threadId: string): Promise<void> {
  const baseUrl = ensureBaseUrl();
  await fetch(`${baseUrl}/threads/${threadId}/lock`, {
    method: 'DELETE',
    headers: await buildHeaders(),
    keepalive: true,
  });
}

export async function deleteThread(threadId: string): Promise<void> {
  const baseUrl = ensureBaseUrl();
  const url = `${baseUrl}/threads/${threadId}`;
  
  const response = await fetchWithTokenRefresh(url, {
    method: 'DELETE',
    headers: await buildHeaders(),
  });
  
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Failed to delete thread: ${response.status} ${text}`);
  }
}
